import { createContext, useContext, useState, useMemo, ReactNode } from 'react'
import { ThemeProvider, createTheme } from '@mui/material/styles'
import { PaletteMode } from '@mui/material'

interface ThemeContextType {
  mode: PaletteMode
  toggleTheme: () => void
}

const ThemeContext = createContext<ThemeContextType>({
  mode: 'light',
  toggleTheme: () => {},
})

// eslint-disable-next-line react-refresh/only-export-components
export const useThemeMode = () => useContext(ThemeContext)

interface ThemeContextProviderProps {
  children: ReactNode
}

export function ThemeContextProvider({ children }: ThemeContextProviderProps) {
  const [mode, setMode] = useState<PaletteMode>(() => {
    // Get saved theme from localStorage or default to light
    const savedMode = localStorage.getItem('themeMode')
    return (savedMode === 'dark' || savedMode === 'light') ? savedMode : 'light'
  })

  const toggleTheme = () => {
    setMode((prevMode) => {
      const newMode = prevMode === 'light' ? 'dark' : 'light'
      localStorage.setItem('themeMode', newMode)
      return newMode
    })
  }

  const theme = useMemo(
    () =>
      createTheme({
        // Vericast brand palette — see docs/vericast-events-brand-guide.md.
        // Light mode uses the corporate tokens directly; dark mode lightens
        // the brand blue for contrast on dark surfaces.
        palette: {
          mode,
          primary: {
            main: mode === 'light' ? '#0075BC' : '#5BA9DC',
            light: mode === 'light' ? '#5BA9DC' : '#7BC0E8',
            dark: mode === 'light' ? '#1C5580' : '#3D6074',
            contrastText: '#FFFFFF',
          },
          secondary: {
            main: mode === 'light' ? '#283138' : '#5BC6CC',
            light: mode === 'light' ? '#51788F' : '#8AD8DC',
            dark: mode === 'light' ? '#1B2228' : '#3E9BA1',
            contrastText: mode === 'light' ? '#FFFFFF' : '#283138',
          },
          info: {
            main: '#5BC6CC',
            contrastText: '#283138',
          },
          warning: {
            main: '#E47720',
            light: '#EF9A55',
            dark: '#C4621B',
          },
          success: {
            main: mode === 'light' ? '#4caf50' : '#66BB6A',
          },
          error: {
            main: mode === 'light' ? '#f44336' : '#EF5350',
          },
          background: {
            default: mode === 'light' ? '#F9FAFF' : '#1B2228',
            paper: mode === 'light' ? '#FFFFFF' : '#283138',
          },
          text: {
            primary: mode === 'light' ? '#283138' : '#FFFFFF',
            secondary: mode === 'light' ? '#51788F' : '#B0BEC5',
          },
          divider: mode === 'light' ? '#CED3D5' : '#3D6074',
        },
        shape: {
          borderRadius: 8,
        },
        typography: {
          // Body = Archivo; headings = Gotham (licensed) with Archivo fallback.
          // See docs/vericast-events-brand-guide.md §2 / §5.3.
          fontFamily: [
            'Archivo',
            'system-ui',
            '-apple-system',
            'BlinkMacSystemFont',
            '"Segoe UI"',
            'Roboto',
            '"Helvetica Neue"',
            'Arial',
            'sans-serif',
          ].join(','),
          button: {
            textTransform: 'none',
            fontWeight: 600,
          },
        },
        components: {
          MuiAppBar: {
            styleOverrides: {
              root: {
                boxShadow: mode === 'light'
                  ? '0px 2px 8px rgba(28, 85, 128, 0.15)'
                  : '0px 2px 8px rgba(0, 0, 0, 0.3)',
              },
              colorSecondary: {
                backgroundColor: mode === 'light' ? '#283138' : '#1B2228',
              },
            },
          },
          MuiButton: {
            styleOverrides: {
              // Pill-shaped brand buttons (brand guide §3).
              root: {
                borderRadius: 9999,
                padding: '8px 22px',
              },
            },
          },
          MuiCard: {
            styleOverrides: {
              root: {
                boxShadow: mode === 'light' 
                  ? '0px 2px 8px rgba(0, 0, 0, 0.08)' 
                  : '0px 2px 8px rgba(0, 0, 0, 0.3)',
                borderRadius: 12,
              },
            },
          },
        },
      }),
    [mode]
  )

  return (
    <ThemeContext.Provider value={{ mode, toggleTheme }}>
      <ThemeProvider theme={theme}>
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  )
}
